/**
 * Platform Detection Utility for Capacitor
 *
 * Provides utilities to detect the current platform (web, iOS, Android)
 * and check for native Capacitor environment.
 */

import { Capacitor } from '@capacitor/core';

/**
 * Check if running in a native Capacitor environment (iOS or Android)
 */
export const isNative = Capacitor.isNativePlatform();

/**
 * Get the current platform
 * @returns 'ios' | 'android' | 'web'
 */
export const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';

/**
 * Check if running on iOS
 */
export const isIOS = platform === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = platform === 'android';

/**
 * Check if running on web (browser)
 */
export const isWeb = platform === 'web';

/**
 * Check if a specific plugin is available
 * @param pluginName Name of the Capacitor plugin
 */
export const isPluginAvailable = (pluginName: string): boolean => {
  return Capacitor.isPluginAvailable(pluginName);
};

/**
 * Get platform-specific value
 * Useful for conditional rendering or behavior based on platform
 */
export function getPlatformValue<T>(options: {
  ios?: T;
  android?: T;
  web?: T;
  native?: T;
  default: T;
}): T {
  if (isIOS && options.ios !== undefined) return options.ios;
  if (isAndroid && options.android !== undefined) return options.android;
  if (isWeb && options.web !== undefined) return options.web;
  if (isNative && options.native !== undefined) return options.native;
  return options.default;
}

/**
 * Log platform info for debugging
 */
export function logPlatformInfo(): void {
  console.log('[Platform] Info:', {
    platform,
    isNative,
    isIOS,
    isAndroid,
    isWeb,
  });
}
